import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Edit, Trash2, Image as ImageIcon } from 'lucide-react';
import { FileUploader } from '@/components/FileUploader';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '@/context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DashboardServices() {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: 'wrench',
    price_range: '',
    duration_minutes: 60,
    faqs: [],
    images: []
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    loadServices();
  }, [navigate]);

  const loadServices = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Error loading services:', error);
      toast.error('Errore nel caricamento dei servizi');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem('token');
      if (editingService) {
        await axios.put(
          `${BACKEND_URL}/api/services/${editingService.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Servizio aggiornato');
      } else {
        await axios.post(
          `${BACKEND_URL}/api/services`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Servizio creato');
      }
      setDialogOpen(false);
      setEditingService(null);
      setFormData({ name: '', description: '', icon: 'wrench', price_range: '', duration_minutes: 60, faqs: [], images: [] });
      loadServices();
    } catch (error) {
      console.error('Error saving service:', error);
      toast.error('Errore nel salvataggio');
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      description: service.description,
      icon: service.icon,
      price_range: service.price_range,
      duration_minutes: service.duration_minutes,
      faqs: service.faqs || [],
      images: service.images || []
    });
    setDialogOpen(true);
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo servizio?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/services/${serviceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Servizio eliminato');
      loadServices();
    } catch (error) {
      console.error('Error deleting service:', error);
      toast.error('Errore nell\'eliminazione');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="pt-32 pb-16 px-4 sm:px-6 lg:px-8" data-testid="dashboard-services">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk' }}>Gestione Servizi</h1>
              <p className="text-gray-600">Aggiungi, modifica o rimuovi servizi</p>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#0066b2] hover:bg-[#005494]" onClick={() => { setEditingService(null); setFormData({ name: '', description: '', icon: 'wrench', price_range: '', duration_minutes: 60, faqs: [], images: [] }); }} data-testid="add-service-button">
                  <Plus className="w-5 h-5 mr-2" /> Aggiungi Servizio
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl" data-testid="service-dialog">
                <DialogHeader>
                  <DialogTitle>{editingService ? 'Modifica Servizio' : 'Nuovo Servizio'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Nome</label>
                    <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} data-testid="service-name-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Descrizione</label>
                    <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={4} data-testid="service-description-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Icona</label>
                      <Input value={formData.icon} onChange={(e) => setFormData({ ...formData, icon: e.target.value })} placeholder="wrench" data-testid="service-icon-input" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Prezzo</label>
                      <Input value={formData.price_range} onChange={(e) => setFormData({ ...formData, price_range: e.target.value })} placeholder="€50 - €100" data-testid="service-price-input" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Durata (minuti)</label>
                    <Input type="number" value={formData.duration_minutes} onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })} data-testid="service-duration-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">{lang === 'en' ? 'Service Images' : 'Immagini Servizio'}</label>
                    <FileUploader accept="image/*" label={lang === 'en' ? 'Upload image' : 'Carica immagine'} onUploadComplete={(url) => setFormData({ ...formData, images: [...formData.images, url] })} />
                    {formData.images.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {formData.images.map((img, i) => (
                          <div key={i} className="relative group">
                            <img src={img} alt="" className="w-16 h-16 object-cover rounded" />
                            <button onClick={() => setFormData({ ...formData, images: formData.images.filter((_, j) => j !== i) })} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">x</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button onClick={handleSubmit} className="w-full bg-[#0066b2] hover:bg-[#005494]" data-testid="save-service-button">
                    {editingService ? 'Aggiorna' : 'Crea'} Servizio
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-12 h-12 border-4 border-[#0066b2] border-t-transparent rounded-full animate-spin" data-testid="services-loading" />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service, idx) => (
                <div key={service.id} className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm" data-testid={`service-item-${idx}`}>
                  <h3 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk' }}>{service.name}</h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">{service.description}</p>
                  <div className="flex items-center justify-between text-sm mb-4">
                    <span className="text-[#0066b2] font-semibold">{service.price_range}</span>
                    <span className="text-gray-500">{service.duration_minutes} min</span>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => handleEdit(service)} className="flex-1" data-testid={`edit-service-${idx}`}>
                      <Edit className="w-4 h-4 mr-1" /> Modifica
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleDelete(service.id)} className="text-red-600 hover:text-red-700" data-testid={`delete-service-${idx}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}